import { CSSProperties, useEffect, useState } from 'react';

import { ColorHarmony, generatePalette } from './color-utils';

// ---------------------------------------------------------------------------
// SVG source geometry — full 7697×5390 canvas as the tile.
// Using the full SVG (not just the 2881×2881 window) keeps all 8 blobs
// proportionally sized and spread across the canvas.
// ---------------------------------------------------------------------------

const SVG_WIDTH = 7697;
const SVG_HEIGHT = 5390;

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = Math.round((SVG_HEIGHT / SVG_WIDTH) * CANVAS_WIDTH); // ≈ 560
const SCALE = CANVAS_WIDTH / SVG_WIDTH; // ≈ 0.1039

// Global blur applied to the whole composition (like the designer's backdrop-filter).
// Orange Lime uses 183px on a 1440px canvas (≈13%). 13% of 800 ≈ 104px.
const GLOBAL_BLUR = 90;

// ---------------------------------------------------------------------------
// Blob definitions — paths and ellipses verbatim from Blobs.svg.
// colorIndex 1 = triadic +120°, colorIndex 2 = triadic +240°.
// (colorIndex 0 = primary, used only as the background fill — blobs on top of
// their own background color would be invisible, so blobs only use 1 or 2.)
// ---------------------------------------------------------------------------

function ellipsePath(cx: number, cy: number, rx: number, ry: number): string {
	return `M ${cx - rx},${cy} A ${rx},${ry},0,1,0,${cx + rx},${cy} A ${rx},${ry},0,1,0,${cx - rx},${cy} Z`;
}

// colorIndex 1, 2, 3 map to complementary palette slots via modulo, so:
//   triadic (2 slots):  1→slot1, 2→slot2, 3→slot1
//   tetradic (3 slots): 1→slot1, 2→slot2, 3→slot3
//   complementary (1 slot): all → slot1
const BLOBS: { d: string; colorIndex: 1 | 2 | 3 }[] = [
	{
		// Blob 0 — organic path, top-left area
		d: 'M1648.03 1087.19C1582.24 757.188 1659.54 224.185 2272.53 96.6864C2885.53 -30.8125 3785.03 614.186 3785.03 614.186C4225.53 967.02 4993.43 1801.69 4541.03 2317.69C4241.18 2659.69 3610.03 2214.19 2925.03 1991.69C2390.53 1818.07 1777.53 1736.69 1648.03 1087.19Z',
		colorIndex: 3,
	},
	{
		// Blob 1 — large ellipse, top-right
		d: ellipsePath(5128, 1795.5, 1276, 1115.5),
		colorIndex: 1,
	},
	{
		// Blob 2 — large ellipse, center-left
		d: ellipsePath(2596, 2650, 1600, 1104),
		colorIndex: 2,
	},
	{
		// Blob 3 — organic path, right-center
		d: 'M4251.18 3637.87C3102.57 3143.92 2755.48 2374.76 3420.83 2172.18C4086.19 1969.61 6096.73 1921.49 7141.85 2340.17C7793.85 2601.36 7463.7 3763.54 7141.85 4119.38C6820 4475.22 5686.95 4255.3 4251.18 3637.87Z',
		colorIndex: 3,
	},
	{
		// Blob 4 — large organic path, right-bottom
		d: 'M4833.53 2665.68C5275.13 2390.48 5825.53 2172.81 6497.53 2265.69C7169.53 2358.56 7811.13 2844.88 7561.53 3561.68C7249.53 4457.68 6113.53 4249.68 5305.53 4425.68C4497.53 4601.68 4545.53 5369.68 3625.53 5305.68C2705.53 5241.68 2249.53 4545.68 2329.53 4001.68C2409.53 3457.68 3009.53 3265.68 3577.53 3257.68C4145.53 3249.68 4281.53 3009.68 4833.53 2665.68Z',
		colorIndex: 1,
	},
	{
		// Blob 5 — organic path, right-bottom overlapping blob 4
		d: 'M3895.25 4299.76C3773.19 3915.71 3895.26 3515.66 4444.53 3259.63C4993.79 3003.6 5344.73 3155.62 5726.17 3051.61C6107.61 2947.59 6210.29 2463.1 6336.48 2179.5C6496.69 1819.46 6893.39 1683.44 7236.68 1899.47C7579.98 2115.49 7747.82 2683.56 7496.06 3467.66C7244.31 4251.75 6557.72 4795.82 5810.09 4987.84C5062.47 5179.86 4047.83 4779.82 3895.25 4299.76Z',
		colorIndex: 2,
	},
	{
		// Blob 6 — organic path, left-bottom
		d: 'M2065.53 2990.18C2603.64 2990.18 3546.03 2990.18 3657.53 3539.18C3769.04 4088.18 3156.03 5059.68 2065.53 5218.68C986.895 5375.95 354.034 4136.18 195.034 3690.68C36.0343 3245.18 -59.467 2520.18 617.033 2345.18C1293.53 2170.18 1527.43 2990.18 2065.53 2990.18Z',
		colorIndex: 1,
	},
	{
		// Blob 7 — center overlay ellipse
		d: ellipsePath(3873, 2426, 956, 880),
		colorIndex: 2,
	},
];

// ---------------------------------------------------------------------------
// Seeded PRNG (LCG)
// ---------------------------------------------------------------------------

function seededRandom(seed: number): () => number {
	let s = seed >>> 0;
	return () => {
		s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
		return s / 0x100000000;
	};
}

function hexToSeed(hex: string): number {
	return parseInt(hex.replace(/^#/, ''), 16) % 0x7fffffff;
}

// ---------------------------------------------------------------------------
// Canvas tile generation
//
// Technique (matches designer's Orange Lime example):
//   1. Fill canvas with primary brand color as background.
//   2. Draw flat blob shapes (no individual blur) using the two triadic colors.
//   3. Stamp the tile 3×3 onto a large canvas.
//   4. Apply one large global blur to the large canvas — this creates the
//      cloud/gradient effect matching the designer's backdrop-filter approach.
//   5. Crop the center tile out (it has correct blur contributions from all
//      8 surrounding tile copies, so edges are seamless when browser tiles it).
//   6. Add a subtle white overlay.
// ---------------------------------------------------------------------------

function generatePatternDataUri(hex: string, harmony: ColorHarmony): string {
	const palette = generatePalette(hex, harmony);
	const primary = palette[0];
	// complementaries are palette[1..N-1]
	const complementaries = palette.slice(1);
	const numComp = complementaries.length;

	// Deterministically shuffle the complementary colors so different brand
	// colors get different slot→blob-group assignments.
	const rng = seededRandom(hexToSeed(hex));
	const slots = [...complementaries];
	for (let i = slots.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[slots[i], slots[j]] = [slots[j], slots[i]];
	}

	// Map a blob's colorIndex (1|2|3) to a shuffled complementary color.
	// Uses modulo so it wraps gracefully when numComp < 3.
	const blobColor = (colorIndex: number) => slots[(colorIndex - 1) % numComp];

	// --- Step 1: unblurred source tile ---
	const source = document.createElement('canvas');
	source.width = CANVAS_WIDTH;
	source.height = CANVAS_HEIGHT;
	const srcCtx = source.getContext('2d');
	if (!srcCtx) return '';

	srcCtx.fillStyle = primary;
	srcCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

	BLOBS.forEach(({ d, colorIndex }) => {
		srcCtx.save();
		srcCtx.fillStyle = blobColor(colorIndex);
		srcCtx.scale(SCALE, SCALE);
		srcCtx.fill(new Path2D(d));
		srcCtx.restore();
	});

	// --- Step 2: stamp 3×3 onto large canvas ---
	const large = document.createElement('canvas');
	large.width = CANVAS_WIDTH * 3;
	large.height = CANVAS_HEIGHT * 3;
	const largeCtx = large.getContext('2d');
	if (!largeCtx) return '';
	for (let tx = 0; tx < 3; tx++) {
		for (let ty = 0; ty < 3; ty++) {
			largeCtx.drawImage(source, tx * CANVAS_WIDTH, ty * CANVAS_HEIGHT);
		}
	}

	// --- Step 3: apply global blur to large canvas ---
	// The blurred canvas is the same size; the blur at the outer edges bleeds
	// into transparent but the CENTER tile (step 4) is 800px from all edges,
	// well beyond the 90px blur radius, so it's fully correct.
	const blurredLarge = document.createElement('canvas');
	blurredLarge.width = CANVAS_WIDTH * 3;
	blurredLarge.height = CANVAS_HEIGHT * 3;
	const blurCtx = blurredLarge.getContext('2d');
	if (!blurCtx) return '';
	blurCtx.filter = `blur(${GLOBAL_BLUR}px)`;
	blurCtx.drawImage(large, 0, 0);

	// --- Step 4: crop center tile ---
	const result = document.createElement('canvas');
	result.width = CANVAS_WIDTH;
	result.height = CANVAS_HEIGHT;
	const resultCtx = result.getContext('2d');
	if (!resultCtx) return '';
	resultCtx.drawImage(blurredLarge, CANVAS_WIDTH, CANVAS_HEIGHT, CANVAS_WIDTH, CANVAS_HEIGHT, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

	// --- Step 5: subtle white overlay (matches Orange Lime's fill-opacity:0.1) ---
	resultCtx.fillStyle = 'rgba(255, 255, 255, 0.08)';
	resultCtx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

	return result.toDataURL('image/png');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ThemePatternProps {
	/** Brand hex color (e.g. "#FF5733"). Drives the palette and background fill. */
	color: string;
	/** Color harmony algorithm. Defaults to triadic. */
	harmony?: ColorHarmony;
	className?: string;
	style?: CSSProperties;
}

export function ThemePattern({ color, harmony = 'triadic', className, style }: ThemePatternProps) {
	const [backgroundImage, setBackgroundImage] = useState('');

	useEffect(() => {
		const dataUri = generatePatternDataUri(color, harmony);
		setBackgroundImage(`url(${dataUri})`);
	}, [color, harmony]);

	return (
		<div
			className={className}
			style={{
				backgroundImage,
				backgroundRepeat: 'repeat',
				backgroundSize: `${CANVAS_WIDTH}px ${CANVAS_HEIGHT}px`,
				...style,
			}}
		/>
	);
}
